import React from 'react';
import { Box, Container, Typography, Paper, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AboutContent: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={6} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Typography variant="h2" component="h1" gutterBottom>
              {t('about.title')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('about.p1')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('about.p2')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('about.p3')}
            </Typography>
            <Button
              component={RouterLink}
              to="/team"
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 2, color: 'text.primary' }}
            >
              {t('about.meetTeam')}
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=600&h=400&fit=crop"
              alt="Desiree African Hair Braiding Interior"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
          </Box>
        </Stack>

        <Box sx={{ my: 8 }}>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('about.valuesTitle')}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            sx={{ mt: 4 }}
            justifyContent="center"
            flexWrap="wrap"
          >
            <Box sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 2,
                  bgcolor: 'primary.light',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <Typography variant="h5" component="h3" gutterBottom>
                  {t('about.quality')}
                </Typography>
                <Typography variant="body1">
                  {t('about.qualityDesc')}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 2,
                  bgcolor: 'primary.light',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <Typography variant="h5" component="h3" gutterBottom>
                  {t('about.personalization')}
                </Typography>
                <Typography variant="body1">
                  {t('about.personalizationDesc')}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 2,
                  bgcolor: 'primary.light',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <Typography variant="h5" component="h3" gutterBottom>
                  {t('about.education')}
                </Typography>
                <Typography variant="body1">
                  {t('about.educationDesc')}
                </Typography>
              </Paper>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ my: 8 }}>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('about.salonTitle')}
          </Typography>
          <Typography variant="body1" align="center" paragraph sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
            {t('about.salonDesc')}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            justifyContent="center"
            flexWrap="wrap"
          >
            <Box sx={{ width: { xs: '100%', sm: '30%' }, mb: { xs: 2, sm: 0 } }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400&h=300&fit=crop"
                alt="Salon Area"
                sx={{ width: '100%', height: 250, objectFit: 'cover', borderRadius: 2, boxShadow: 1 }}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '30%' }, mb: { xs: 2, sm: 0 } }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=400&h=300&fit=crop"
                alt="Styling Station"
                sx={{ width: '100%', height: 250, objectFit: 'cover', borderRadius: 2, boxShadow: 1 }}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '30%' }, mb: { xs: 2, sm: 0 } }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1562322140-8baeececf3df?w=400&h=300&fit=crop"
                alt="Washing Area"
                sx={{ width: '100%', height: 250, objectFit: 'cover', borderRadius: 2, boxShadow: 1 }}
              />
            </Box>
          </Stack>
        </Box>

        <Box sx={{ my: 8, textAlign: 'center' }}>
          <Typography variant="h3" component="h2" gutterBottom>
            {t('about.ctaTitle')}
          </Typography>
          <Typography variant="body1" paragraph sx={{ maxWidth: 700, mx: 'auto' }}>
            {t('about.ctaDesc')}
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 2, color: 'text.primary' }}
          >
            {t('about.ctaButton')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutContent;
